import { SrPartialPackingTypeMap } from '@plato/data/starrocks/types';
import { ChevronLeftIcon, ChevronRightIcon, PackageXIcon, PlusIcon, RefreshCcwIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { IncrementalQuantityField } from '../../../components/forms/incremental-quantity-field.tsx';
import { Button } from '../../../components/ui/button.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { GetCatalogProducts } from '../../../server/types/infer-trpc-types.ts';
import { checkQuantityForRounding } from '../utils/catalog-quantity-utils.ts';

type Product = GetCatalogProducts['mainProduct'];

type ProductRowProps = {
    isLoading: boolean;
    pageCount: number;
    product?: Product | null;
    onAddProduct: (quantity: number) => void;
    onNavigateBack: () => void;
    onNavigateForward: () => void;
    children: ReactNode;
};

type ProductDetails = {
    productVariantId: string;
    productVariantName: string;
    quantity: number;
    units: string;
    salesUnitsPerPackageUnit: number;
    partialPackingType: SrPartialPackingTypeMap;
};

export const AddProductFromCatalog: React.FC<ProductRowProps> = ({
    isLoading,
    product,
    onAddProduct,
    onNavigateBack,
    onNavigateForward,
    children,
}) => {
    const { t } = useLocale();

    const [productDetails, setProductDetails] = useState<ProductDetails | null>(null);

    useEffect(() => {
        if (product && product.product_variant_id !== productDetails?.productVariantId) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- legacy code
            setProductDetails({
                productVariantId: product.product_variant_id,
                productVariantName: product.product_variant_name,
                quantity: product.sales_units_per_package_unit || 1,
                units: product.quantity_unit_sales || '',
                salesUnitsPerPackageUnit: product.sales_units_per_package_unit || 1,
                partialPackingType: product.partial_packing_type ?? 'UNKNOWN',
            });
        } else {
            setProductDetails(null);
        }
    }, [product]);

    const canEditNumber = !!productDetails && !isLoading;

    const canSubmit = useMemo(() => {
        if (isLoading) {
            return false;
        }
        if (!productDetails) {
            return false;
        }
        const { quantity, salesUnitsPerPackageUnit, partialPackingType } = productDetails;
        if (!quantity) {
            return false;
        }
        const checkedQuantity =
            checkQuantityForRounding(quantity, salesUnitsPerPackageUnit, partialPackingType) ?? 0;
        if (checkedQuantity !== quantity) {
            return false;
        }

        if (
            partialPackingType === SrPartialPackingTypeMap.NO_PACKAGE_BREAK &&
            quantity % salesUnitsPerPackageUnit !== 0
        ) {
            return false;
        }

        return true;
    }, [productDetails, isLoading]);

    const onSubmit = () => {
        if (!productDetails) {
            return false;
        }
        const { quantity, salesUnitsPerPackageUnit, partialPackingType } = productDetails;
        if (!quantity) {
            return;
        }
        const checkedQuantity =
            checkQuantityForRounding(quantity, salesUnitsPerPackageUnit, partialPackingType) ?? 0;
        setProductDetails({
            ...productDetails,
            quantity: checkedQuantity,
        });
        onAddProduct(checkedQuantity);
    };

    const onChangeValue = (value: number) => {
        if (!productDetails) {
            return;
        }
        setProductDetails({
            ...productDetails,
            quantity: value,
        });
    };

    return (
        <>
            <div className="flex shrink-0 items-center gap-2 border-b border-neutral-200 px-3 py-2.5 pr-14">
                <div className="flex shrink-0 items-center gap-2">
                    <Button size="icon" variant="subtle" onClick={onNavigateBack}>
                        <ChevronLeftIcon className="size-4 text-neutral-700" />
                    </Button>
                    <Button size="icon" variant="subtle" onClick={onNavigateForward}>
                        <ChevronRightIcon className="size-4 text-neutral-700" />
                    </Button>
                </div>

                <div className="flex h-10 min-w-0 flex-1 items-center px-1">
                    {isLoading ? (
                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
                            <div className="flex items-center gap-1 text-neutral-400">
                                <RefreshCcwIcon className="size-4 shrink-0 animate-spin" />
                                <div className="h-3 w-12 animate-pulse rounded bg-neutral-200" />
                            </div>
                            <div className="flex items-center">
                                <div className="h-3 w-40 max-w-full animate-pulse rounded bg-neutral-200" />
                            </div>
                        </div>
                    ) : productDetails ? (
                        <div className="flex min-w-0 flex-1 flex-col justify-center gap-1 leading-tight">
                            <div className="flex min-w-0 items-center gap-1 text-neutral-500">
                                <PackageXIcon className="size-4 shrink-0" />
                                <span className="truncate text-xs">{productDetails.productVariantId}</span>
                            </div>
                            <div className="flex min-w-0 items-center">
                                <span className="truncate text-sm text-neutral-900">
                                    {productDetails.productVariantName}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex min-w-0 items-center gap-1 text-neutral-400">
                            <PackageXIcon className="size-4 shrink-0" />
                            <span className="truncate text-sm">
                                {t('no_product_found', { ns: 'catalog' })}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {children}

            <div className="flex w-full shrink-0 items-center justify-end gap-3 border-t border-neutral-100 px-4 py-3">
                <IncrementalQuantityField
                    className="w-[150px]"
                    key={canEditNumber ? product?.product_variant_id : 'no-product'}
                    value={productDetails ? productDetails.quantity : null}
                    onValueChange={onChangeValue}
                    disabled={!canEditNumber}
                    partialPackingType={productDetails?.partialPackingType}
                    quantityUnit={productDetails?.units ?? ''}
                    salesUnitsPerPackageUnit={productDetails?.salesUnitsPerPackageUnit}
                />

                <Button variant="primary" disabled={!canSubmit} onClick={onSubmit}>
                    <PlusIcon className="size-4" />
                    {t('add_product', { ns: 'catalog' })}
                </Button>
            </div>
        </>
    );
};
